// BRIEF 118 §6 (the extraction half) — THE MEETING DIALOG'S GROUPED ROSTER, ONCE.
//
// §6 names this builder as the thing a third caller must reuse: "the same
// grouped attendee roster the parties-pane seed builds — reuse that builder, do
// not write a second one; if it is inline in CaseView, extract it so both
// callers share it (cure before improvement: extraction commit, then use)."
//
// It was inline in BOTH, and the two spellings are byte-identical:
//
//   src/views/CaseView.tsx              `grps.flatMap(…)`, inline in the button
//   src/views/CollectiveCaseView.tsx    `bA()`, a local closure
//
// WHAT IS SHARED AND WHAT IS NOT. The flatten-filter-map is shared and moves
// here. The GROUPS do not: each matter kind resolves its own people from its
// own id families (a case reads `clIds`/`opIds`/…; a collective case reads the
// same families off `ccIds` plus its own), and the two label the groups
// differently — CaseView carries two English literals where CCV carries `t()`
// keys. Pulling the groups in would have meant choosing between those, which is
// an improvement, and rule 3 says moving and bettering are two commits. The
// caller passes its groups; this owns the shape of what comes out.
//
// EXACT, not equivalent: same field order, same `p && p.email` filter (a person
// with no address cannot be invited and is silently absent, as before), same
// `checked` from the group's own `precheck`, same `group` label carried onto
// every row so `AttendeesPickerSection` can section them again.

/** A person as either matter pane resolves one; only three fields are read. */
export type MeetingPerson = {
  id?: unknown;
  email?: string | null;
  [k: string]: unknown;
};

/** One section of the picker: its label, its people, and whether they start
 *  ticked (the internal team does; every external party does not). */
export type MeetingGroup = {
  label: string;
  persons: readonly (MeetingPerson | null | undefined)[];
  precheck: boolean;
  /* 119 §5 — DOES THIS GROUP'S `id` MEAN A COLLABORATOR? Additive and optional,
     so the shape is a union of what callers pass and not a replacement. The
     matter panes assemble their cast from several tables whose ids overlap, and
     `meetingRoster` below must not read a Client id as a Collaborator id — so
     the caller, which is the only thing that knows which table a group came
     from, says. Absent = not collaborators, which is the safe answer. */
  isCollaborator?: boolean;
};

export type MeetingAttendee = {
  id: unknown;
  name: string;
  email: string;
  checked: boolean;
  group: string;
};

/**
 * Flatten the groups into the dialog's attendee list.
 * `name` is the caller's `pname` — the same resolver both panes already hold.
 */
export function meetingAttendees(
  groups: readonly MeetingGroup[],
  name: (p: MeetingPerson) => string,
): MeetingAttendee[] {
  return groups.flatMap((g) =>
    (g.persons || [])
      .filter((p): p is MeetingPerson => !!p && !!p.email)
      .map((p) => ({
        id: p.id,
        name: name(p),
        email: String(p.email),
        checked: g.precheck,
        group: g.label,
      })),
  );
}

// ── BRIEF 119 §5 — THREE NAMED GROUPS, ONE GROUPING FUNCTION ────────────────
//
// [RULED — Laurent, 2026-08-23] "The producer GROUPS a population it is handed;
// it never decides which population. The three labels are a property of the
// PERSON relative to the MATTER, not of the event."
//
//   (a) team  — on the matter's case team. Invited automatically.
//   (b) firm  — an in-app collaborator NOT on that team.
//   (c) third — no collaborator id at all: clients, opposing counsel, experts,
//               any external address. They hold only the calendar invitation.
//
// TWO CALLERS, ONE FUNCTION:
//
//   · the modal overview and the badge hover panel hand it the EVENT's people →
//     three groups of who is ON it, each row carrying §6's response status.
//   · the dialog roster hands it the MATTER's cast (plus the event's people, so
//     it can mark them) → the SAME three groups of who COULD be invited, each
//     row carrying its already-invited check state.
//
// ROWS DIFFER BY WHAT THEY CARRY, NEVER BY HOW THEY ARE GROUPED. Somebody in the
// roster's population but not the event's is NOT a fourth group — he is an
// unchecked row. That is why this is generic in the row type and reads exactly
// two fields off it: a person's group is decided by `id` and nothing else, so no
// caller can accidentally make it depend on what its own rows happen to carry.
//
// §5 says this file "already knows the matter's team". It does not, and cannot:
// the team is resolved by the caller (`cs.caseCollaborators`), which is also
// what lets one producer serve a case, a collective case and a matter-less
// internal meeting (empty team → every collaborator lands in (b), which is
// correct rather than a special case).

/** Which of the three. The caller supplies the words; see `PEOPLE_GROUP_KEY`. */
export type PeopleGroupKey = "team" | "firm" | "third";

/**
 * The ONE field the grouping reads. Everything else a row carries is the
 * caller's and passes through untouched.
 *
 * `coId`, DELIBERATELY NOT `id`. The dialog's population is the matter's cast,
 * where a row's `id` is a Client id, an Opponent id or an Expert id — different
 * tables, overlapping integers. Grouping on `id` would read client 3 as
 * collaborator 3 and file a client under "Case team", which is the cross-table
 * id-collision class the 2026-07-13 pass cleaned out of the financial filters.
 * `coId` means "the collaborator this row IS", null for everyone who is not
 * one, and a caller that forgets to set it gets (c) — the safe answer.
 */
export type GroupablePerson = { coId?: number | null };

export type PeopleGroup<P> = { key: PeopleGroupKey; people: P[] };

/** The i18n key per group, so three consumers cannot name them three ways. */
export const PEOPLE_GROUP_KEY: Record<PeopleGroupKey, string> = {
  team: "people.group.case_team",
  firm: "people.group.firm_invited",
  third: "people.group.third_parties",
};

/* NO `fallback:` LITERALS FOR THESE THREE, and the reason is `i18n-total`'s
   GATE 1 rather than taste: a fallback map here is three English sentences in a
   library file, reaching a human at a registered exit. They are seeded in
   en/fr/nl, and an unseeded key renders as the bare key — a loud signal — which
   is the same call `alerts.close_via_bell_tip` made (App.tsx, brief 42 §1.3).
   Consumers call `t(PEOPLE_GROUP_KEY[k])` with no second argument. */

/** A person's group, relative to one matter's team. The whole rule, in one
 *  place: no id at all is a third party; an id on the team is (a); any other
 *  id is (b). */
export function peopleGroupOf(
  person: GroupablePerson | null | undefined,
  teamIds: ReadonlySet<number>,
): PeopleGroupKey {
  const n = Number(person?.coId);
  if (person?.coId == null || !Number.isFinite(n)) return "third";
  return teamIds.has(n) ? "team" : "firm";
}

/** The matter's team as a set, from whatever shape the caller holds it in. */
export function teamIdSet(ids: readonly unknown[] | null | undefined): Set<number> {
  const out = new Set<number>();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const n = Number(raw);
    if (Number.isFinite(n)) out.add(n);
  }
  return out;
}

/**
 * Group a population into the three, in the fixed order team → firm → third.
 * EMPTY GROUPS ARE OMITTED ENTIRELY (§5), which is why this returns a list and
 * not a fixed triple: a consumer that renders what it is given cannot forget
 * the rule. Order WITHIN a group is the population's own.
 */
export function groupPeopleByMatter<P extends GroupablePerson>(
  population: readonly P[],
  teamIds: ReadonlySet<number>,
): PeopleGroup<P>[] {
  const buckets: Record<PeopleGroupKey, P[]> = { team: [], firm: [], third: [] };
  for (const p of population || []) {
    if (!p) continue;
    buckets[peopleGroupOf(p, teamIds)].push(p);
  }
  const order: PeopleGroupKey[] = ["team", "firm", "third"];
  return order
    .filter((k) => buckets[k].length > 0)
    .map((k) => ({ key: k, people: buckets[k] }));
}

/**
 * BRIEF 119 §5 — THE DIALOG'S ROSTER: the matter's cast, in the SAME three
 * groups, each row carrying its already-invited check state.
 *
 * [RULED] "Someone in the roster's population but not the event's is not a
 * fourth group — he is an unchecked row." That is this function's whole shape:
 * `meetingAttendees` flattens the cast exactly as before, `groupPeopleByMatter`
 * decides the three, and `checked` is the only thing the event's own people
 * change.
 *
 * THE CHECK RULE, and it is behaviour-preserving for the caller that exists
 * today: the case team is checked (group (a) is "invited automatically"), and
 * everybody else is checked iff their address is already on the event. Opened
 * from the parties pane there IS no event, so `invited` is empty and the result
 * is precisely the old `precheck` — team ticked, parties not.
 *
 * @param label resolves a group key to words; the caller holds `t`, this does not.
 */
/* ── BRIEF 122 §1 — THE CALLER'S FAMILY SURVIVES FOR A NON-COLLABORATOR ──────
   Every caller hands this four labelled party families — Clients, Opponents,
   Opposing counsel, Third parties — and until 122 they were flattened into the
   single key `third`, so every external person read "THIRD PARTIES". That is
   Laurent's complaint on the 2a-i glance, and it was one function discarding
   information its callers had already computed.

   THE TYPE DECISION, WHICH §1 ASKS BE STATED: the label is CARRIED BESIDE THE
   KEY. `PeopleGroupKey` stays the closed union of three it is today.

   Widening it was the other legal option and it is the wrong one here, for a
   reason that is structural rather than stylistic: the family labels are the
   CALLER'S and are not a closed set — CaseView passes four, CollectiveCaseView
   passes four different ones (and 122 §4 changes one of them). A union cannot
   express "whatever the caller said", so widening would have to either make
   `PEOPLE_GROUP_KEY` a partial record — losing the exhaustiveness its readers
   rely on when they index it by key (`App.tsx`'s badge hover panel, and the
   `label` callback every roster caller passes) — or invent fixed keys for
   families this library does not own. And §1 keeps the three
   groups CORRECT for the overview: `peopleGroupOf`'s id rule is not replaced,
   it decides (a) and (b) and defers to the label only for (c). Two orthogonal
   facts about a person — his app-relationship and the family his matter pane
   filed him under — stay two fields. */
type RosterRow = {
  id: unknown;
  coId: number | null;
  name: string;
  email: string;
  /** The caller's own family label, kept for the (c) case. */
  family: string;
};

export function meetingRoster(
  groups: readonly MeetingGroup[],
  name: (p: MeetingPerson) => string,
  teamIds: ReadonlySet<number>,
  invited: ReadonlySet<string>,
  label: (key: PeopleGroupKey) => string,
): MeetingAttendee[] {
  const rows: RosterRow[] = groups.flatMap((g) =>
    (g.persons || [])
      .filter((p): p is MeetingPerson => !!p && !!p.email)
      .map((p) => {
        const co = g.isCollaborator ? Number(p.id) : NaN;
        return {
          id: p.id,
          coId: Number.isFinite(co) ? co : null,
          name: name(p),
          email: String(p.email),
          family: g.label,
        };
      }),
  );
  return groupPeopleByMatter(rows, teamIds).flatMap((g) =>
    g.people.map((r) => ({
      id: r.id,
      name: r.name,
      email: r.email,
      checked: g.key === "team" || invited.has(r.email.trim().toLowerCase()),
      /* (a) and (b) are app-relationships and keep §5's words; (c) is a party
         and keeps his family's. A caller that passes no label for a group of
         non-collaborators gets the §5 word back, which is the old behaviour and
         the safe answer. */
      group: g.key === "third" ? r.family || label(g.key) : label(g.key),
    })),
  );
}

// ── BRIEF 122 §2 — ONE LIST, AND IT IS A UNION OF *THREE* POPULATIONS ───────
//
// §2 rules the New Event modal's three people surfaces into one checkbox list
// and names the population "the UNION of the matter's cast and the event's own
// people". MEASURED, there are THREE, and the third is the one a two-way union
// would have silently dropped:
//
//   1. the matter's CAST        `meetingRoster` — carries §1's family sections
//                               and the pre-check rule.
//   2. the event's OWN PEOPLE   `eventPeople` — carries §6's response status,
//                               and is the only population holding an external
//                               address that was invited but is not in the cast.
//   3. every CHOOSABLE COLLABORATOR — the population the `MultiSelect` "Should
//      attend" offered (`choosableCo(collaborators, …)`, AgendaEventModal:743).
//      It is the whole firm, not the matter's team, and dropping it would take
//      away the ability to put an event on the agenda of a colleague who is
//      neither on the case team nor already invited. §2's own rule is that
//      nothing displayed today may stop being displayed, and rule 2 says the
//      union is what every call site actually passes.
//
// THE KEY IS `coId` WHERE THERE IS ONE AND THE LOWERCASED ADDRESS OTHERWISE —
// the identity the deleted `EventPeopleOverview` keyed its rows by, carried
// forward rather than reinvented. It cannot be the address alone: a collaborator
// may have none, and two populations reach the same colleague by different
// routes (assigned by id, invited by address) — the id↔address join at
// `eventPeople`'s own rule is what makes those one row instead of two.
//
// ROWS MERGE, THEY DO NOT COMPETE. A person in the cast AND on the event is one
// row taking its SECTION from the cast (which knows his family) and its
// RESPONSE from the event (which is the only thing that has one). That is what
// lets §2's "response status rides the ROW" be true without anything ceasing to
// be displayed: it is a field on a row, not a second list.

export type AttendeeRow = {
  /** Stable identity across the three populations; see `attendeeKey`. */
  key: string;
  id: unknown;
  coId: number | null;
  name: string;
  email: string;
  checked: boolean;
  /** The section heading — §1's family, or §5's word for the firm's own. */
  group: string;
  response: string | null;
  respondedAt: string | null;
};

export function attendeeKey(coId: number | null | undefined, email: unknown): string {
  return coId == null ? "e:" + lcEmail(email) : "c:" + coId;
}

export function mergeAttendeeRows<C extends EventCollaborator>(args: {
  cast: readonly MeetingAttendee[];
  people: readonly EventPersonRow[];
  /** Population 3 — whoever may still be CHOSEN. May be empty (the internal
   *  meeting narrows its own roster and that narrowing is deliberate). */
  firm: readonly C[];
  /** EVERY collaborator, for the id↔address join alone — never a population.
   *  Separate from `firm` because a caller may offer a narrowed roster while
   *  still needing a colleague's address to resolve to his id. */
  collaborators: readonly C[];
  teamIds: ReadonlySet<number>;
  /** `AgendaEvent.assigned` as the form holds it — the stored truth on an edit. */
  assigned: readonly unknown[] | null | undefined;
  /** The addresses the row has already invited (119 §6's photograph). */
  invited: ReadonlySet<string>;
  /** No `form.id` yet: the team's pre-check is the default rather than a lie. */
  isNew: boolean;
  name: (c: C) => string;
  label: (key: PeopleGroupKey) => string;
}): AttendeeRow[] {
  const { cast, people, firm, collaborators, teamIds, invited, isNew, name, label } =
    args;

  const byEmail = new Map<string, C>();
  for (const c of collaborators || []) {
    const e = lcEmail(c?.email);
    if (e && !byEmail.has(e)) byEmail.set(e, c);
  }
  const assignedSet = new Set<number>();
  for (const raw of Array.isArray(args.assigned) ? args.assigned : []) {
    const n = Number(raw);
    if (Number.isFinite(n)) assignedSet.add(n);
  }
  const answered = new Map<string, EventPersonRow>();
  for (const r of people || []) {
    if (r) answered.set(attendeeKey(r.coId, r.email), r);
  }

  /* THE CHECK RULE, and the one place the two modals differ in substance.
     On a NEW event the team's pre-check is the default — "invited
     automatically" is what group (a) means. On an EXISTING one the STORED
     truth wins: a colleague taken off the event must not come back ticked
     every time the modal reopens, which is what §3a's "unticking removes"
     would otherwise be unable to survive. */
  const isChecked = (coId: number | null, email: string, castChecked: boolean) =>
    coId == null
      ? invited.has(lcEmail(email)) || (isNew && castChecked)
      : assignedSet.has(coId) ||
        (isNew && (castChecked || teamIds.has(coId)));

  const out: AttendeeRow[] = [];
  const seen = new Map<string, AttendeeRow>();
  const put = (row: AttendeeRow) => {
    const prior = seen.get(row.key);
    if (prior) {
      /* FIRST ARRIVAL OWNS IDENTITY AND SECTION — the cast walks first, so a
         person his matter pane can name keeps that name and that family.
         A later population may still contribute what the earlier one lacks. */
      if (!prior.response && row.response) {
        prior.response = row.response;
        prior.respondedAt = row.respondedAt;
      }
      prior.checked = prior.checked || row.checked;
      return;
    }
    seen.set(row.key, row);
    out.push(row);
  };

  // 1 — the cast, in the caller's own order, so §2's sections come out in the
  //     parties pane's order without anything having to sort them.
  for (const a of cast || []) {
    const co = byEmail.get(lcEmail(a.email));
    const coId = co && Number.isFinite(Number(co.id)) ? Number(co.id) : null;
    const key = attendeeKey(coId, a.email);
    const ans = answered.get(key);
    put({
      key,
      id: a.id,
      coId,
      name: a.name,
      email: a.email,
      checked: isChecked(coId, a.email, !!a.checked),
      /* §1's RULE, APPLIED ONCE FOR ALL THREE POPULATIONS. A row that resolves
         to a collaborator is (a) or (b) whatever family a caller filed him
         under — 119 §5's "the email is the join, so a colleague invited by
         ADDRESS lands in (b) rather than appearing as a third party in his own
         firm". Only somebody with no collaborator id keeps his caller's family.
         Deciding it here rather than trusting the cast's own `group` is also
         what lets a caller pass a bare roster with no labels at all (the
         internal meeting does) and still get sections. */
      group:
        coId != null
          ? label(peopleGroupOf({ coId }, teamIds))
          : a.group || label("third"),
      response: ans?.response ?? null,
      respondedAt: ans?.respondedAt ?? null,
    });
  }

  // 2 — the event's own people: whoever the cast did not already name. An
  //     external address invited but not in the cast lives ONLY here.
  for (const r of people || []) {
    if (!r) continue;
    const key = attendeeKey(r.coId, r.email);
    put({
      key,
      id: r.coId ?? r.email,
      coId: r.coId,
      name: r.name,
      email: r.email,
      checked: isChecked(r.coId, r.email, false),
      group: label(peopleGroupOf({ coId: r.coId }, teamIds)),
      response: r.response,
      respondedAt: r.respondedAt,
    });
  }

  // 3 — the rest of the firm, so the multi-select's reach survives the merge.
  for (const c of firm || []) {
    const n = Number(c?.id);
    if (!Number.isFinite(n)) continue;
    const key = attendeeKey(n, c?.email);
    put({
      key,
      id: n,
      coId: n,
      name: name(c),
      email: String(c?.email || ""),
      checked: isChecked(n, String(c?.email || ""), false),
      group: label(peopleGroupOf({ coId: n }, teamIds)),
      response: null,
      respondedAt: null,
    });
  }

  /* SECTION ORDER, and it is an enumerated choice rather than an accident.
     §2 asks for "Case team first, then Clients, Opponents, Opposing counsel,
     Third parties" — the parties pane's own order, which the cast already
     arrives in. It says nothing about §5's group (b), because (b) is not a
     party family; it is the firm's own people who are not on this matter's
     team. They go SECOND, beside the team, rather than wherever the third
     population happened to be walked — a heading of colleagues sitting between
     Clients and Opponents reads as a party family and is not one.

     A stable sort on rank alone, so within every section the order is still the
     population's own (§5: "Order WITHIN a group is the population's own"). */
  const rank = (g: string) =>
    g === label("team") ? 0 : g === label("firm") ? 1 : 2;
  return out
    .map((r, i) => ({ r, i }))
    .sort((a, b) => rank(a.r.group) - rank(b.r.group) || a.i - b.i)
    .map((x) => x.r);
}

/* ── BRIEF 137 §4(c) — THE INVITATION DRAFT'S TWO LINES ─────────────────────
 *
 * [RULED, Laurent 2026-08-25; 138 §2 lifts 137's HARD STOP 1 for a regular
 * case] Whether mail leaves follows from WHO IS ON THE EVENT and never from
 * which button was pressed. An event carrying at least one person who is not a
 * firm user opens ONE Outlook draft:
 *
 *     to  = the external participants   (the `third` rows)
 *     cc  = the internal participants   (the `team` + `firm` rows)
 *
 * `cc` is Laurent's own reason — "they should be in cc of the email responses
 * if any" — and `to` rather than `bcc` is 138 §2's ruling: the 122 fence is
 * about a collective case's CLAIMANT ROSTER, and two named parties invited to
 * one meeting are about to sit in the same room.
 *
 * IT LIVES HERE BECAUSE THE PREDICATE DOES. `coId == null` IS `third`
 * (`peopleGroupOf` above, and the reason that field is `coId` and not `id`),
 * and the merged roster is the one population that has already resolved it for
 * all three sources. A caller re-deriving "is this person external" from a
 * family label would be reading the caller's words instead of the join.
 *
 * EMPTY `to` MEANS NO DRAFT, and that is the whole of §4(c)'s other half: an
 * internal event sends nothing, silently and by construction. The caller tests
 * `to.length`, so the decision is never a second flag.
 */
export function inviteDraftRecipients(rows: readonly AttendeeRow[]): {
  to: string[];
  cc: string[];
} {
  const to: string[] = [];
  const cc: string[] = [];
  const seen = new Set<string>();
  for (const r of rows || []) {
    if (!r || !r.checked) continue;
    const e = String(r.email || "").trim();
    /* No address, no line — the same silence `meetingAttendees` has applied
       since 118 §6 ("a person with no address cannot be invited and is
       silently absent"). */
    if (!e) continue;
    /* ONE LINE PER ADDRESS, and `to` wins. A colleague a matter pane also
       filed under a party family would otherwise be on two lines of the same
       message; being an addressee is the stronger statement. Keyed
       case-insensitively — the identity `attendeeKey` uses — while the
       spelling that goes out is the one the roster holds. */
    const k = lcEmail(e);
    if (seen.has(k)) continue;
    seen.add(k);
    (r.coId == null ? to : cc).push(e);
  }
  return { to, cc };
}

/** The ticked rows that are collaborators — §2: "`assignedCollaborators` is
 *  what the ticked TEAM/FIRM rows produce." */
export function assignedFromRows(rows: readonly AttendeeRow[]): number[] {
  return [
    ...new Set(
      (rows || [])
        .filter((r) => r && r.checked && r.coId != null)
        .map((r) => Number(r.coId)),
    ),
  ];
}

// ── THE EVENT'S POPULATION, BUILT ONCE ──────────────────────────────────────
//
// The grouping above never decides a population; this decides ONE of them, and
// it is a separate named function for the reason §5 gives about the grouping
// itself: the modal overview and the hover panel must not answer "who is on
// this event" two ways. The dialog's population is the matter's cast and is
// assembled by the matter pane, which is the only place that knows its own id
// families.

/** One stored addressee — 119 §6's `AgendaEvent.outlookAttendees` shape. */
export type OutlookAttendee = {
  email?: string | null;
  name?: string | null;
  type?: string | null;
  /** Graph's own vocabulary: none | organizer | tentativelyAccepted | accepted | declined. */
  response?: string | null;
  respondedAt?: string | null;
};

/** A collaborator, as far as this file needs to know one. */
export type EventCollaborator = { id?: number | null; email?: string | null };

/** What the overview and the panel render, one row per person. */
export type EventPersonRow = {
  coId: number | null;
  name: string;
  email: string;
  /** What Outlook said at the last look; null = not in the photograph at all. */
  response: string | null;
  respondedAt: string | null;
};

const lcEmail = (v: unknown) => String(v || "").trim().toLowerCase();

/**
 * Initials for somebody who is NOT a collaborator. `coIni` (src/utils.ts:592)
 * reads a stored `initials` column and falls back to `firstName[0]+lastName[0]`;
 * an external addressee has neither — all Graph gives is a display name and an
 * address, and half the time the display name IS the address. So: the first
 * letter of up to two words, and if that yields nothing (an address with no
 * name), the first two letters of the local part.
 */
export function externalInitials(nameOrEmail: string): string {
  const raw = String(nameOrEmail || "").trim();
  if (!raw) return "?";
  const local = raw.includes("@") ? raw.split("@")[0] : raw;
  const words = local.split(/[\s._-]+/).filter(Boolean);
  /* ONE WORD YIELDS TWO LETTERS, not one. `info@x.be` has no second word, and a
     badge reading "I" is noise where "IN" is at least a handle. A collaborator
     with only a first name gets one letter from `coIni` and that is fine — the
     firm knows its own people — but nobody recognises an external party from a
     single initial. */
  const ini =
    words.length > 1
      ? words.slice(0, 2).map((w) => w[0]).join("")
      : (words[0] || local).slice(0, 2);
  return (ini || "?").toUpperCase();
}

/**
 * Who is ON this event.
 *
 * THE TEAM IS IN THE POPULATION WHETHER OR NOT ANYBODY TICKED THEM. §5's own
 * words for group (a) are "invited automatically", so listing only the ticked
 * ones would make the group a lie about who receives it.
 *
 * THE JOIN BETWEEN THE TWO SIDES IS THE EMAIL, lowercased: LexCRM knows
 * collaborator ids, Graph knows addresses, and the address is the only identity
 * both hold. A stored address that resolves to a collaborator is therefore a
 * firm member however it got onto the event — never a third party.
 *
 * @param teamIds       the matter's own collaborator ids
 * @param assigned      `AgendaEvent.assigned` — the collaborators on this event
 * @param stored        `AgendaEvent.outlookAttendees`, or null when never looked
 * @param collaborators every in-app collaborator, for the id↔address join
 * @param name          the caller's own `pname`, so one resolver names everyone
 */
export function eventPeople<C extends EventCollaborator>(
  teamIds: ReadonlySet<number>,
  assigned: readonly unknown[] | null | undefined,
  stored: readonly OutlookAttendee[] | null | undefined,
  collaborators: readonly C[],
  name: (c: C) => string,
): EventPersonRow[] {
  const byId = new Map<number, C>();
  const byEmail = new Map<string, C>();
  for (const c of collaborators || []) {
    const n = Number(c?.id);
    if (Number.isFinite(n)) byId.set(n, c);
    const e = lcEmail(c?.email);
    if (e && !byEmail.has(e)) byEmail.set(e, c);
  }
  const resp = new Map<string, OutlookAttendee>();
  for (const a of Array.isArray(stored) ? stored : []) {
    const e = lcEmail(a?.email);
    if (e && !resp.has(e)) resp.set(e, a);
  }

  const ids: number[] = [];
  const seenId = new Set<number>();
  const pushId = (raw: unknown) => {
    const n = Number(raw);
    if (!Number.isFinite(n) || seenId.has(n)) return;
    seenId.add(n);
    ids.push(n);
  };
  for (const n of teamIds) pushId(n);
  for (const raw of Array.isArray(assigned) ? assigned : []) pushId(raw);
  /* A colleague invited THROUGH THE DIALOG arrives as an address, not as an
     assignment. Resolving those back to collaborators is what stops him
     appearing as a third party in his own firm. */
  for (const e of resp.keys()) {
    const c = byEmail.get(e);
    if (c) pushId(Number(c.id));
  }

  const out: EventPersonRow[] = [];
  for (const n of ids) {
    const c = byId.get(n);
    if (!c) continue;
    const e = lcEmail(c.email);
    const a = e ? resp.get(e) : undefined;
    out.push({
      coId: n,
      name: name(c),
      email: String(c.email || ""),
      response: (a && typeof a.response === "string" && a.response) || null,
      respondedAt: (a && a.respondedAt) || null,
    });
  }
  /* (c) by construction: a stored address that resolves to no collaborator. */
  for (const [e, a] of resp) {
    if (byEmail.has(e)) continue;
    out.push({
      coId: null,
      name: String(a?.name || "").trim() || e,
      email: e,
      response: (typeof a?.response === "string" && a.response) || null,
      respondedAt: a?.respondedAt || null,
    });
  }
  return out;
}
